// cohorts/_shared types: the cohort as the API returns it, used by the list, the create answer and
// the cohort page. Shape of `cohort_payload` (cohorts-v1.md), checked against the backend code on
// 4 Nov 2026. Zod schemas are the contract, parsed at the service boundary.
import { z } from "zod";

/** One appel (recruitment wave) of a cohort; only sent to the President and the responsible admin. */
export const CohortAppelSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  wave_number: z.number(),
  status: z.string(),
  opening_at: z.string().nullable(),
  closing_at: z.string().nullable(),
  application_count: z.number(),
  submitted_count: z.number(),
});

export type CohortAppel = z.infer<typeof CohortAppelSchema>;

export const CohortSchema = z.object({
  id: z.string(),
  /** Generated by the server (`COH-000001`), read-only. */
  code: z.string(),
  name: z.string(),
  /** `PREPARING` or `ARCHIVED`; kept open so a later status does not break the page. */
  status: z.string(),
  places: z.number(),
  waitlist_places: z.number(),
  /** Optional settings: `null` until the responsible Administrator sets them. */
  interview_margin_percent: z.number().nullable().optional(),
  response_delay_days: z.number().nullable().optional(),
  program_start_date: z.string().nullable(),
  program_end_date: z.string().nullable(),
  /** Sent back as `expected_revision` on every change. */
  revision: z.number(),
  archived_at: z.string().nullable(),
  /** The four headline numbers of the cohort page. */
  dashboard_counts: z.object({
    submitted: z.number(),
    receivable: z.number(),
    to_process: z.number(),
    places: z.number(),
  }),
  /** Only for the President and the responsible Administrator. */
  rounds: z.array(CohortAppelSchema).optional(),
  /** The next appel to create (`Appel 2`); only for the President and the responsible Administrator. */
  next_appel: z
    .object({ wave_number: z.number(), name: z.string() })
    .optional(),
  /** Only for the President (and an account allowed to assign coordinators). */
  responsible_administrator: z
    .object({ id: z.string(), full_name: z.string() })
    .nullable()
    .optional(),
  /**
   * What this account may do with this cohort (`view`, `rename`, `edit`, `archive`, `restore`,
   * `appoint_responsible_administrator`, `manage_coordinators`, `create_appel`). The server only
   * advertises what is allowed. Read it with `hasAction` from `@core/permissions`.
   */
  available_actions: z.record(z.string(), z.boolean()),
});

export type Cohort = z.infer<typeof CohortSchema>;

/**
 * Whether this account should see the cohort at all. An archived cohort is only for the account
 * that can restore it (the President): the API still serves it read-only to the responsible
 * Administrator, but the app sends them back to their list. A convenience, not security: the
 * server stays the authority on what is readable.
 */
export function isVisibleToViewer(
  cohort: Pick<Cohort, "status" | "available_actions">
): boolean {
  return !isArchived(cohort) || cohort.available_actions.restore === true;
}

/** `PREPARING` is « En préparation »; anything else is shown as archived. */
export function isArchived(cohort: Pick<Cohort, "status">): boolean {
  return cohort.status === "ARCHIVED";
}
